/**
 * Show Translation Card Component
 *
 * Displays and manages a single show translation.
 * Shows name, description, search_terms fields with announcement audio.
 *
 * @module ShowTranslationCard
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import { Sparkles, Check, AlertCircle } from 'lucide-react';
import { useNotify, useRefresh } from 'react-admin';
import type { ShowTranslation, Language } from '@musedrops/shared';
import {
  getMediaUrl,
  getConventionalFilename,
  logger,
} from '@musedrops/shared';
import { AudioFileField } from '../../../shared/components/forms/AudioFileField';
import { EntityEnabledToggle } from '../../../shared/components/EntityEnabledToggle';
import { supabase } from '../../../services/supabase';
import {
  getShowTranslation,
  updateShowTranslation,
  triggerTranslationGeneration,
  triggerAnnounceGeneration,
} from '../../../data/showTranslation';

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type TranslationUpdatedHandler = (value: ShowTranslation) => void;

interface ShowTranslationCardProps {
  showId: string;
  showSlug: string;
  language: Language;
  translation: ShowTranslation | null;
  onTranslationUpdated: TranslationUpdatedHandler;
}

/**
 * Card component for managing a single show translation
 */
export const ShowTranslationCard: React.FC<ShowTranslationCardProps> = ({
  showId,
  showSlug,
  language,
  translation,
  onTranslationUpdated,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();

  // Local form state
  const [name, setName] = useState(translation?.name || '');
  const [description, setDescription] = useState(translation?.description || '');
  const [searchTerms, setSearchTerms] = useState(translation?.searchTerms || '');
  const [audioLogoDesc, setAudioLogoDesc] = useState(translation?.audioLogoDesc || '');
  const [announceUpdatedAt, setAnnounceUpdatedAt] = useState(
    translation?.announceUpdatedAt || null
  );

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [activeTextJobId, setActiveTextJobId] = useState<number | null>(null);
  const [activeAudioJobId, setActiveAudioJobId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [enabledBusy, setEnabledBusy] = useState(false);

  // Sync local state when translation prop changes
  useEffect(() => {
    setName(translation?.name || '');
    setDescription(translation?.description || '');
    setSearchTerms(translation?.searchTerms || '');
    setAudioLogoDesc(translation?.audioLogoDesc || '');
    setAnnounceUpdatedAt(translation?.announceUpdatedAt || null);
    setHasChanges(false);
  }, [translation]);

  // Track changes
  useEffect(() => {
    const changed =
      name !== (translation?.name || '') ||
      description !== (translation?.description || '') ||
      searchTerms !== (translation?.searchTerms || '') ||
      audioLogoDesc !== (translation?.audioLogoDesc || '');
    setHasChanges(changed);
  }, [name, description, searchTerms, audioLogoDesc, translation]);

  // Subscribe to text generation job updates
  useEffect(() => {
    if (!activeTextJobId) return undefined;

    logger.info('Subscribing to text job updates', { jobId: activeTextJobId });

    const subscription = supabase
      .channel(`text-job-${activeTextJobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'process_queue',
          filter: `id=eq.${activeTextJobId}`,
        },
        async payload => {
          const { status } = payload.new as { status: string; last_error?: string };
          logger.info('Text job status update', { jobId: activeTextJobId, status });

          if (status === 'completed') {
            setIsGeneratingText(false);
            setActiveTextJobId(null);
            // Reload translation data from DB
            try {
              const updated = await getShowTranslation(showSlug, language.key);
              if (updated) {
                onTranslationUpdated(updated);
              }
            } catch (err) {
              logger.warn('Failed to reload translation after generation', { err });
            }
          } else if (status === 'failed') {
            const lastError = (payload.new as { last_error?: string }).last_error;
            setError(`Translation generation failed: ${lastError || 'Unknown error'}`);
            setIsGeneratingText(false);
            setActiveTextJobId(null);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [activeTextJobId, showSlug, language.key, onTranslationUpdated]);

  // Subscribe to audio generation job updates
  useEffect(() => {
    if (!activeAudioJobId) return undefined;

    logger.info('Subscribing to audio job updates', { jobId: activeAudioJobId });

    const subscription = supabase
      .channel(`audio-job-${activeAudioJobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'process_queue',
          filter: `id=eq.${activeAudioJobId}`,
        },
        payload => {
          const { status } = payload.new;
          logger.info('Audio job status update', { jobId: activeAudioJobId, status });

          if (status === 'completed') {
            setIsGeneratingAudio(false);
            setActiveAudioJobId(null);
            // Update local timestamp - actual file is available
            setAnnounceUpdatedAt(new Date().toISOString());
          } else if (status === 'failed') {
            setError(`Audio generation failed: ${payload.new.error}`);
            setIsGeneratingAudio(false);
            setActiveAudioJobId(null);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [activeAudioJobId]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    setError(null);

    try {
      const updated = await updateShowTranslation(showSlug, language.key, {
        name: name || undefined,
        description: description || undefined,
        searchTerms: searchTerms || undefined,
        audioLogoDesc: audioLogoDesc || undefined,
      });
      onTranslationUpdated(updated);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [hasChanges, showSlug, language.key, name, description, searchTerms, audioLogoDesc, onTranslationUpdated]);

  // Handle generate translation text
  const handleGenerateText = useCallback(async () => {
    setIsGeneratingText(true);
    setError(null);

    try {
      const result = await triggerTranslationGeneration(showId, language.key, true);
      setActiveTextJobId(result.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate');
      setIsGeneratingText(false);
    }
  }, [showId, language.key]);

  // Handle generate announcement audio
  const handleGenerateAudio = useCallback(async () => {
    setIsGeneratingAudio(true);
    setError(null);

    try {
      const result = await triggerAnnounceGeneration(showId, language.key, true);
      setActiveAudioJobId(result.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate audio');
      setIsGeneratingAudio(false);
    }
  }, [showId, language.key]);

  // Compute status
  const hasText = !!(translation?.name && translation?.description);
  const hasAudio = !!announceUpdatedAt;
  const isComplete = hasText && hasAudio;

  // Audio URL
  const announceFile = getConventionalFilename('show', 'showAnnounce', language.key);
  const audioUrl = announceUpdatedAt
    ? `${getMediaUrl('shows', showSlug, announceFile)}?t=${new Date(announceUpdatedAt).getTime()}`
    : undefined;

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2 }}
    >
      <CardContent>
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{language.name}</Typography>
            <Chip
              size="small"
              label={language.key}
              variant="outlined"
              sx={{ ml: 1 }}
            />
            {isComplete ? (
              <Chip
                size="small"
                icon={<Check size={14} />}
                label="Complete"
                color="success"
              />
            ) : hasText ? (
              <Chip
                size="small"
                icon={<AlertCircle size={14} />}
                label="Missing Audio"
                color="warning"
              />
            ) : (
              <Chip
                size="small"
                label="Not Generated"
                color="default"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Generate button */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerateText}
              disabled={isGeneratingText}
              startIcon={
                isGeneratingText ? (
                  <CircularProgress size={14} />
                ) : (
                  <Sparkles size={14} />
                )
              }
            >
              {isGeneratingText ? 'Generating...' : 'Generate'}
            </Button>
          </Box>
        </Box>

        {/* Issue #767: Per-language Enabled toggle as its own full-width row */}
        <EntityEnabledToggle
          enabled={translation?.enabled !== false}
          disabledReason={translation?.disabledReason}
          disabledAt={translation?.disabledAt}
          busy={enabledBusy}
          sx={{ mb: 2 }}
          onToggle={async (next, reason) => {
            setEnabledBusy(true);
            try {
              if (next) {
                const { data, error: invokeError } =
                  await supabase.functions.invoke('reenable-entity', {
                    body: {
                      entityType: 'show_translation',
                      entityId: `${showId}:${language.key}`,
                    },
                  });
                if (invokeError || !data?.success) {
                  throw new Error(
                    invokeError?.message || data?.error || 'Re-enable failed'
                  );
                }
              } else {
                await updateShowTranslation(showSlug, language.key, {
                  enabled: false,
                  disabledAt: new Date().toISOString(),
                  disabledReason: reason || 'Manually disabled',
                });
              }
              // Reflect new state in the card
              try {
                const updated = await getShowTranslation(
                  showSlug,
                  language.key
                );
                if (updated) onTranslationUpdated(updated);
              } catch (reloadErr) {
                logger.warn('Failed to reload translation after toggle', {
                  reloadErr,
                });
              }
              notify(next ? 'Language enabled' : 'Language disabled', {
                type: 'success',
              });
              refresh();
            } catch (err) {
              notify(err instanceof Error ? err.message : 'Update failed', {
                type: 'error',
              });
            } finally {
              setEnabledBusy(false);
            }
          }}
        />

        {/* Content */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            placeholder={`Translated show name in ${language.name}`}
          />

          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder={`Show description in ${language.name}`}
          />

          <TextField
            label="Search Terms"
            value={searchTerms}
            onChange={e => setSearchTerms(e.target.value)}
            fullWidth
          />

          <TextField
            label="Audio Logo Description"
            value={audioLogoDesc}
            onChange={e => setAudioLogoDesc(e.target.value)}
            fullWidth
          />
        </Box>

        {/* Save Button */}
        {hasChanges && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}

        {/* Announcement Audio Section */}
        <Box>
          <AudioFileField
            label={`Announcement (${language.key})`}
            value={announceUpdatedAt ? announceFile : null}
            onUpload={() => {
              // Upload handled by AudioFileField
            }}
            onRegenerate={handleGenerateAudio}
            isRegenerating={isGeneratingAudio}
            RegenerateIcon={Sparkles as React.ComponentType<{ size?: number }>}
            regenerateTitle={`Generate ${language.name} voice announcement with AI`}
            onDelete={() => {
              setAnnounceUpdatedAt(null);
            }}
            onTimestampChanged={timestamp => {
              setAnnounceUpdatedAt(timestamp);
            }}
            audioUrl={audioUrl}
            storageFolder="shows"
            slug={showSlug}
            entityId={showSlug}
            tableName="show_translations"
            fileType="showAnnounce"
            lang={language.key}
            accept="audio/*"
            showGenerate
          />
        </Box>
      </CardContent>
    </Card>
  );
};
